/**
 * ComparisonResponseCard Component
 *
 * Displays a response card within the Comparison Detail view.
 * Shows winner with gold border + star, removes provider label,
 * displays "Response Time" instead of "ms", and makes winning score prominent.
 * Clicking navigates to /results#view=responses&id={responseId}
 */

import React from 'react';
import { getModelLogo } from '../../../utils/logoMapping';
import { getResponseContent } from '../../../utils/responseContent';
import type { Response } from '../../../lib/types/results';
import type { DensityOption } from '../state/density';
import { VisualRenderer } from '../responses/VisualRenderer';
import { CriteriaBadges, type CriteriaScores } from './CriteriaBadges';
import { logEvent } from '../../../utils/telemetry';

export type ScoreChipVariant =
  | 'legacy'
  | 'winner-trophy-solid'
  | 'nonwinner-neutral'
  | 'nonwinner-muted-icon';

export interface ComparisonResponseCardProps {
  response: Response;
  isWinner?: boolean;
  isHighestScore?: boolean; // True when this response has the highest score
  score?: number;
  onClick?: () => void;
  density?: DensityOption;
  // Task 4: SxS Compare Deck selection
  isInCompareDeck?: boolean;
  onAddToCompare?: (responseId: string) => void;
  onRemoveFromCompare?: (responseId: string) => void;
  canAddToCompare?: boolean; // False when deck is full (2 items)
  isSxSMode?: boolean; // True when in side-by-side comparison view
  // Criteria scores for badge display
  criteriaScores?: Partial<CriteriaScores>;
  // Presentation variant for future winner/non-winner chip treatments
  scoreChipVariant?: ScoreChipVariant;
  // Winner vs. non-winner role for future chip presentation logic
  scoreRole?: 'winner' | 'nonwinner';
  // Metadata for telemetry
  comparisonId?: string;
  comparisonCorrelationId?: string;
}

interface ScoreChipProps {
  variant: ScoreChipVariant;
  score: number;
  role: 'winner' | 'nonwinner';
}

const ScoreChip: React.FC<ScoreChipProps> = ({ variant, score, role }) => {
  const formattedScore = Number.isFinite(score) ? score.toFixed(1) : '—';
  const baseClasses =
    'inline-flex items-center justify-center gap-1 rounded-full px-3 py-1 text-sm font-semibold tracking-tight';

  const variantConfig: Record<ScoreChipVariant, { className: string; icon?: React.ReactNode }> = {
    legacy: { className: '' },
    'winner-trophy-solid': {
      className: 'bg-amber-400 text-slate-900 shadow-lg shadow-amber-500/30',
      icon: (
        <span aria-hidden="true" className="text-base leading-none">
          🏆
        </span>
      ),
    },
    'nonwinner-neutral': {
      className: 'bg-slate-800/90 text-slate-100 border border-slate-600/60 shadow-inner shadow-black/20',
    },
    'nonwinner-muted-icon': {
      className: 'bg-slate-800/90 text-slate-100 border border-slate-600/60 shadow-inner shadow-black/20',
      icon: (
        <svg
          aria-hidden="true"
          className="w-4 h-4 text-slate-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4l2.09 4.24 4.68.68-3.39 3.33.8 4.65L12 14.77 7.82 16.9l.8-4.65L5.23 8.92l4.68-.68z"
          />
        </svg>
      ),
    },
  };

  const ariaLabel = role === 'winner' ? `Winner, score ${formattedScore}` : `Score ${formattedScore}, not winner`;

  return (
    <div className={`${baseClasses} ${variantConfig[variant].className}`} aria-label={ariaLabel} title={ariaLabel}>
      {variantConfig[variant].icon}
      <span>{formattedScore}</span>
    </div>
  );
};

export const ComparisonResponseCard: React.FC<ComparisonResponseCardProps> = ({
  response,
  isWinner = false,
  isHighestScore = false,
  score = 0,
  onClick,
  density = 'stacked',
  isInCompareDeck = false,
  onAddToCompare,
  onRemoveFromCompare,
  canAddToCompare = true,
  isSxSMode = false,
  criteriaScores,
  scoreChipVariant = 'legacy',
  scoreRole = 'nonwinner',
  comparisonId,
  comparisonCorrelationId,
}) => {
  const responseContent = getResponseContent(response);
  const isCompactView = density === 'compact' || density === 'comfortable';
  const chipTelemetryRef = React.useRef<string>('');
  const hasScore = typeof score === 'number' && score > 0;
  const shouldRenderScoreChip = scoreChipVariant !== 'legacy' && hasScore;
  const shouldRenderLegacyScore = hasScore && !shouldRenderScoreChip;

  // State for resizable content height
  const [contentHeight, setContentHeight] = React.useState<number>(10.8); // 10.8rem = ~7 lines default (20% taller)
  const contentRef = React.useRef<HTMLDivElement>(null);

  // State for drag-to-resize functionality
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartY, setDragStartY] = React.useState(0);
  const [dragStartHeight, setDragStartHeight] = React.useState(10.8);

  // Truncate content in grid views for better visual comparison
  const contentPreview = isCompactView
    ? responseContent.slice(0, 150)
    : responseContent.slice(0, 300);

  const chipTelemetryKey = shouldRenderScoreChip && score
    ? `${response.id}-${scoreChipVariant}-${scoreRole}-${score.toFixed(3)}-${density}-${isSxSMode ? 'sxs' : 'stacked'}`
    : '';

  // Track content height changes from user resizing
  React.useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        // Convert px to rem (assuming 16px = 1rem)
        const heightInRem = height / 16;
        setContentHeight(heightInRem);
      }
    });

    resizeObserver.observe(contentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Reset to default height on double-click
  const handleResetHeight = () => {
    setContentHeight(9); // Reset to 6 lines (9rem)
  };

  // Handle resize drag
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent card click
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartHeight(contentHeight);
  };

  // Track mouse movement during drag
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - dragStartY;
      const deltaRem = deltaY / 16; // Convert px to rem
      const newHeight = Math.max(9, Math.min(36, dragStartHeight + deltaRem));
      setContentHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartY, dragStartHeight]);

  React.useEffect(() => {
    if (!shouldRenderScoreChip || !score) {
      chipTelemetryRef.current = '';
      return;
    }

    if (chipTelemetryRef.current === chipTelemetryKey) {
      return;
    }

    chipTelemetryRef.current = chipTelemetryKey;

    logEvent({
      op: 'cdv_score_chip_render',
      component: 'ComparisonResponseCard',
      correlationId: comparisonCorrelationId,
      comparison_id: comparisonId,
      response_id: response.id,
      score_role: scoreRole,
      score_value: Number(score.toFixed(3)),
      score_display: score.toFixed(1),
      variant: scoreChipVariant,
      route: '/results',
      layout: isSxSMode ? 'side-by-side' : 'stacked',
      density,
    });
  }, [
    chipTelemetryKey,
    comparisonCorrelationId,
    comparisonId,
    density,
    isSxSMode,
    response.id,
    score,
    scoreChipVariant,
    scoreRole,
    shouldRenderScoreChip,
  ]);

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`group relative bg-slate-800/30 backdrop-blur-sm border rounded-xl ${isCompactView ? 'p-4' : 'p-5'} hover:border-cyan-500/30 transition-all cursor-pointer ${
        isHighestScore
          ? 'border-yellow-500/60 ring-2 ring-yellow-500/30 shadow-lg shadow-yellow-500/10'
          : isWinner
          ? 'border-cyan-400/60 ring-2 ring-cyan-400/30 shadow-lg shadow-cyan-500/10'
          : 'border-slate-700/50'
      }`}
      aria-label={response.modelId}
      role="button"
      tabIndex={0}
      data-score-chip-variant={scoreChipVariant}
      data-score-role={scoreRole}
    >
      {/* Trophy/Badge - Gold trophy for highest score, cyan badge for API winner */}
      {!shouldRenderScoreChip && (isWinner || isHighestScore) && (
        <div className={`absolute top-2 right-2 z-10 rounded-full p-1.5 shadow-lg ring-2 ring-slate-900 ${
          isHighestScore ? 'bg-yellow-500' : 'bg-cyan-500'
        }`}>
          {isHighestScore ? (
            // Trophy icon for highest score - Award with Ribbons
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 7a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 3.5-1.323 2.68-2.957.43 2.14 2.085-.505 2.946L12 17.25l2.645 1.39-.505-2.945 2.14-2.086-2.957-.43L12 10.5zM18 2v3l-1.363 1.138A9.935 9.935 0 0 0 13 5.049L13 2zm-7-.001v3.05a9.935 9.935 0 0 0-3.636 1.088L6 5V2z"/>
            </svg>
          ) : (
            // Star icon for API winner
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex items-center space-x-2">
          {/* Model Logo */}
          <div className="w-8 h-8 bg-slate-700/30 rounded-lg flex items-center justify-center p-1">
            <img
              src={getModelLogo(response.modelId, response.providerName || response.provider?.name || response.providerId)}
              alt={`${response.modelId} logo`}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Model Name (No provider label per requirements) */}
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold group-hover:text-cyan-300 transition-colors ${
              isWinner ? 'text-base text-white' : 'text-sm text-white'
            }`}>
              {response.model?.displayName || response.modelId}
            </h4>
            {/* FIX #3: Show error message for failed responses */}
            {response.status === 'failed' && response.errorMessage && (
              <p className="text-xs text-red-400 mt-1">
                ⚠️ {response.errorMessage}
              </p>
            )}

            {/* Criteria Badges - hidden on redesigned cards so they only appear in the detail header */}
          </div>
        </div>

        {/* Score - Larger and more prominent for winner */}
        {shouldRenderScoreChip && score && (
          <div className="flex-shrink-0">
            <ScoreChip variant={scoreChipVariant} score={score} role={scoreRole} />
          </div>
        )}
        {shouldRenderLegacyScore && score && (
          <div className="flex items-center space-x-1">
            <svg className={`text-yellow-400 ${isWinner ? 'w-5 h-5' : 'w-4 h-4'}`} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className={`font-semibold text-yellow-400 ${isWinner ? 'text-lg' : 'text-xs'}`}>
              {score.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Response Content Preview */}
      {/* Desktop: Scrollable content area with 6 lines visible (resizable) */}
      <div className="hidden md:block relative mb-3">
        <div
          ref={contentRef}
          className="text-sm text-slate-300 overflow-y-auto response-scroll-area pr-2"
          style={{
            minHeight: '9rem', // 6 lines minimum
            maxHeight: '36rem', // 24 lines maximum
            height: `${contentHeight}rem`,
            lineHeight: '1.5rem',
          }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={handleResetHeight}
          title="Drag bottom edge to resize, double-click to reset"
        >
          <VisualRenderer content={responseContent || 'No response available'} className="text-sm" />
        </div>

        {/* Fade gradient overlay at bottom (indicates more content) */}
        {responseContent.length > 400 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-slate-800/90 via-slate-800/50 to-transparent pointer-events-none" />
        )}

        {/* Interactive resize handle - Full width draggable area (desktop only) */}
        <div
          onMouseDown={handleResizeMouseDown}
          className="hidden md:flex absolute bottom-0 left-0 right-0 h-8 cursor-ns-resize items-center justify-center hover:bg-slate-700/40 transition-all group"
          onClick={(e) => e.stopPropagation()}
          title="Drag to resize"
        >
          {/* Visual indicator bars */}
          <div className="flex flex-col gap-1.5 pointer-events-none">
            <div className="w-20 h-1 bg-slate-400 group-hover:bg-slate-300 rounded-full transition-colors" />
            <div className="w-20 h-1 bg-slate-400 group-hover:bg-slate-300 rounded-full transition-colors" />
          </div>
        </div>
      </div>

      {/* Mobile: Keep existing clamped behavior */}
      <div className="md:hidden text-sm text-slate-300 mb-3">
        <div className="line-clamp-3 overflow-hidden">
          <VisualRenderer content={contentPreview || 'No response available'} className="text-sm" />
        </div>
      </div>

      {/* Footer Metadata - Changed "ms" to "Response Time" */}
      <div className="flex items-center space-x-3 text-xs text-slate-500">
        {response.tokenCount && <span>{response.tokenCount} tokens</span>}
        {response.latencyMs && (
          <>
            <span>•</span>
            <span>Response Time: {(response.latencyMs / 1000).toFixed(2)}s</span>
          </>
        )}
        {/* Removed AI score from footer per requirements */}
      </div>

      {/* Task 4 & View Details: Combined button row (desktop-only) */}
      {(onAddToCompare || onRemoveFromCompare) && (
        <div className={`hidden md:flex w-full mt-3 ${isCompactView ? 'flex-col gap-1' : 'flex-row items-center gap-2'}`}>
          {/* Add/Remove Compare button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isInCompareDeck && onRemoveFromCompare) {
                onRemoveFromCompare(response.id);
              } else if (!isInCompareDeck && onAddToCompare && canAddToCompare) {
                onAddToCompare(response.id);
              }
            }}
            disabled={!isInCompareDeck && !canAddToCompare}
            className={`${isCompactView ? 'w-full' : 'flex-1'} flex items-center justify-center gap-1.5 px-3 py-1.5 border rounded text-xs font-medium transition-all ${
              isInCompareDeck
                ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-300 hover:bg-cyan-500/30 hover:border-cyan-400/70'
                : canAddToCompare
                ? 'bg-slate-700/30 hover:bg-slate-700/50 border-slate-600/30 hover:border-cyan-500/50 text-slate-300 hover:text-cyan-300'
                : 'bg-slate-800/30 border-slate-700/30 text-slate-500 cursor-not-allowed'
            }`}
            aria-label={isInCompareDeck ? 'Remove from compare deck' : 'Add to side-by-side comparison'}
          >
            {isInCompareDeck ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Selected for Side-by-Side
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {canAddToCompare ? 'Side By Side' : 'Side-by-Side full'}
              </>
            )}
          </button>

          {/* View Full Details button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onClick?.();
              }
            }}
            className={`${isCompactView ? 'w-full' : 'flex-1'} flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/30 hover:border-cyan-500/50 rounded text-xs font-medium text-slate-300 hover:text-cyan-300 transition-all group-hover:border-cyan-500/30`}
            aria-label="View full response details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View details
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
